import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Shield, Users, Search, RefreshCw, ChevronDown, ChevronUp,
  Mail, Phone, Building2, CreditCard, AlertTriangle, X, Loader2, Crown,
  CheckCircle2, Clock
} from "lucide-react";
import { BackToDashboard } from "../components/BackToDashboard";

const PLAN_OPTIONS = ["free", "starter", "pro", "agency"];

// Truncated cell with hover tooltip for long content
function TruncCell({ value, maxW = "max-w-[160px]", mono = false }) {
  const [hovered, setHovered] = React.useState(false);
  if (!value || value === "—") return <span className="text-zinc-400">—</span>;
  return (
    <span className="relative inline-block">
      <span
        className={`block truncate ${maxW} ${mono ? "font-mono" : ""} cursor-default`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {value}
      </span>
      {hovered && (
        <span className="absolute left-0 top-full mt-1.5 z-50 bg-zinc-900 text-white text-xs rounded-sm px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-xs pointer-events-none" style={{lineHeight:"1.4"}}>
          {value}
        </span>
      )}
    </span>
  );
}


const PLAN_COLORS = {
  free:    { bg: "bg-zinc-100",  text: "text-zinc-700",  label: "Free Trial" },
  starter: { bg: "bg-blue-50",   text: "text-blue-700",  label: "Starter"    },
  pro:     { bg: "bg-amber-50",  text: "text-amber-700", label: "Pro"        },
  agency:  { bg: "bg-emerald-50",text: "text-emerald-700",label: "Agency"    },
  admin:   { bg: "bg-purple-50", text: "text-purple-700", label: "Admin"     },
};

function PlanBadge({ plan }) {
  const c = PLAN_COLORS[plan] || PLAN_COLORS.free;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-wider ${c.bg} ${c.text}`}>
      {plan === "admin" && <Crown size={10} />}
      {c.label}
    </span>
  );
}


export default function AdminPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [toast, setToast] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/users?limit=500");
      setUsers(r.data.users || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate("/dashboard");
        return;
      }
      setError(e?.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadUsers(); }, [loadUsers]);


  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = users
    .filter(u => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.company || "").toLowerCase().includes(q) ||
        (u.phone || "").includes(q)
      );
    })
    .sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";
      if (sortKey === "created_at") {
        av = new Date(av).getTime() || 0;
        bv = new Date(bv).getTime() || 0;
      } else {
        av = String(av).toLowerCase();
        bv = String(bv).toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ChevronDown size={13} className="text-zinc-300" />;
    return sortDir === "asc"
      ? <ChevronUp size={13} className="text-zinc-700" />
      : <ChevronDown size={13} className="text-zinc-700" />;
  };

  const planCounts = users.reduce((acc, u) => {
    const p = u.is_admin ? "admin" : (u.plan || "free");
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});

  const errorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    return detail?.message || fallback;
  };

  const updatePlan = async (user, plan) => {
    if (!user || user.is_admin || plan === (user.plan || "free")) return;
    setBusyUserId(user.user_id);
    try {
      await api.patch(`/admin/users/${user.user_id}/plan`, { plan });
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, plan } : u));
      showToast(`Updated ${user.email} to ${PLAN_COLORS[plan]?.label || plan}.`);
    } catch (e) {
      showToast(errorMessage(e, "Could not update plan."), "error");
    } finally {
      setBusyUserId(null);
    }
  };

  const cancelSubscription = async (user) => {
    if (!user || user.is_admin) return;
    const ok = window.confirm(`Cancel ${user.email}'s subscription and move them to Free?`);
    if (!ok) return;
    setBusyUserId(user.user_id);
    try {
      const r = await api.post(`/admin/users/${user.user_id}/cancel-subscription`);
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? {
        ...u,
        plan: "free",
        plan_renews_at: null,
        subscription_cancelled_at: new Date().toISOString(),
      } : u));
      showToast(r.data?.paddle_cancelled ? "Subscription cancelled in Paddle and user moved to Free." : "User moved to Free. No Paddle subscription was found.");
    } catch (e) {
      showToast(errorMessage(e, "Could not cancel subscription."), "error");
    } finally {
      setBusyUserId(null);
    }
  };

  const AdminActions = ({ user }) => {
    if (user.is_admin) {
      return <span className="text-xs text-zinc-400">Protected admin</span>;
    }
    const busy = busyUserId === user.user_id;
    const currentPlan = user.plan || "free";
    return (
      <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2">
        <select
          value={currentPlan}
          disabled={busy}
          onChange={e => updatePlan(user, e.target.value)}
          className="h-8 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950 disabled:opacity-50"
          aria-label={`Change plan for ${user.email}`}
        >
          {PLAN_OPTIONS.map(plan => (
            <option key={plan} value={plan}>{PLAN_COLORS[plan]?.label || plan}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || currentPlan === "free"}
          onClick={() => cancelSubscription(user)}
          className="h-8 rounded-sm border border-red-200 px-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          {busy ? "Working..." : "Cancel"}
        </button>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto">
      <BackToDashboard />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 bg-purple-600 rounded-sm flex items-center justify-center">
              <Shield size={14} className="text-white" />
            </div>
            <span className="label-eyebrow text-purple-700">Admin Panel</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            User Management
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            {total} total user{total !== 1 ? "s" : ""} registered
          </p>
        </div>
        <button
          onClick={loadUsers}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Total", value: total, icon: Users, color: "text-zinc-700" },
          { label: "Free Trial", value: planCounts.free || 0, icon: Clock, color: "text-zinc-600" },
          { label: "Starter", value: planCounts.starter || 0, icon: CheckCircle2, color: "text-blue-600" },
          { label: "Pro", value: planCounts.pro || 0, icon: CreditCard, color: "text-amber-600" },
          { label: "Agency", value: planCounts.agency || 0, icon: Building2, color: "text-emerald-600" },
          { label: "Admins", value: planCounts.admin || 0, icon: Crown, color: "text-purple-600" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{label}</span>
              <Icon size={13} className={color} strokeWidth={1.5} />
            </div>
            <div className="font-display text-2xl font-bold tracking-tight mt-2">{loading ? "—" : value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="text"
          placeholder="Search by name, email, company, or phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-zinc-200 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-transparent"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
            <X size={14} />
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm mb-4">
          <AlertTriangle size={14} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                {[
                  { key: "name", label: "Name" },
                  { key: "email", label: "Email" },
                  { key: "company", label: "Company" },
                  { key: "phone", label: "Phone" },
                  { key: "plan", label: "Plan" },
                  { key: "created_at", label: "Joined" },
                  { key: "actions", label: "Actions" },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== "actions" && toggleSort(key)}
                    className={`text-left px-4 py-3 label-eyebrow select-none ${key === "actions" ? "" : "cursor-pointer hover:text-zinc-900"}`}
                  >
                    <div className="flex items-center gap-1">
                      {label} {key !== "actions" && <SortIcon k={key} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-zinc-400">
                    <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                    Loading users…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-zinc-400">
                    {search ? "No users match your search." : "No users found."}
                  </td>
                </tr>
              ) : filtered.map(u => (
                <tr key={u.user_id} className={`hover:bg-zinc-50 transition-colors ${u.is_admin ? "bg-purple-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.is_admin && <Crown size={12} className="text-purple-500 shrink-0" />}
                      <TruncCell value={u.name || "—"} maxW="max-w-[140px]" />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Mail size={12} className="text-zinc-300 shrink-0" />
                      <TruncCell value={u.email} maxW="max-w-[200px]" mono />
                      {u.is_google_user && (
                        <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-sm uppercase tracking-wider shrink-0">G</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600"><TruncCell value={u.company || "—"} maxW="max-w-[140px]" /></td>
                  <td className="px-4 py-3 font-mono text-zinc-600 text-xs">{u.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <PlanBadge plan={u.is_admin ? "admin" : (u.plan || "free")} />
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <AdminActions user={u} />
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden divide-y divide-zinc-100">
          {loading ? (
            <div className="py-12 text-center text-zinc-400">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Loading users…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-zinc-400">
              {search ? "No users match your search." : "No users found."}
            </div>
          ) : filtered.map(u => (
            <div key={u.user_id} className={`p-4 ${u.is_admin ? "bg-purple-50/40" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {u.is_admin && <Crown size={12} className="text-purple-500 shrink-0" />}
                    <span className="font-semibold text-zinc-900 truncate">{u.name || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="font-mono text-xs text-zinc-600 truncate">{u.email}</span>
                    {u.is_google_user && (
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-sm uppercase tracking-wider shrink-0">G</span>
                    )}
                  </div>
                </div>
                <PlanBadge plan={u.is_admin ? "admin" : (u.plan || "free")} />
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-500 mb-3">
                {u.company && (
                  <div className="flex items-center gap-1.5">
                    <Building2 size={11} className="shrink-0" />
                    <span className="truncate">{u.company}</span>
                  </div>
                )}
                {u.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={11} className="shrink-0" />
                    <span className="font-mono">{u.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Clock size={11} className="shrink-0" />
                  <span>{fmtDate(u.created_at)}</span>
                </div>
              </div>
              <AdminActions user={u} />

            </div>
          ))}
        </div>

        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="border-t border-zinc-100 px-4 py-2.5 bg-zinc-50 text-xs text-zinc-400">
            Showing {filtered.length} of {total} user{total !== 1 ? "s" : ""}
            {search && ` matching "${search}"`}
          </div>
        )}
      </div>


      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-sm shadow-lg text-sm font-semibold border
            ${toast.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
            }`}
        >
          {toast.type === "error"
            ? <AlertTriangle size={14} className="shrink-0" />
            : <CheckCircle2 size={14} className="shrink-0" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
